import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { IAccountUnitMappingExtended } from "@app/interfaces/IAccountUnitMappingExtended";
import { IUnit } from "@app/interfaces/IPeripheral";
import { IBasicProductModel } from "@app/interfaces/IProductUiViewModel";
import { IVehicleUnitMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { IVehicleUnitMappingExtended } from "@app/interfaces/IVehicleUnitMappingExtended";
import { AccountFeatureService } from "@app/services/accountFeature.service";
import { DateService } from "@app/services/date.service";
import { ProductsService } from "@app/services/products.service";
import { TranslationService } from "@app/services/translation.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { IConfigMappings, IFirmwareMappings } from "../../../_services/peripheral.service.ajs";

@Injectable({
    providedIn: 'root'
})
export class HardwareMappingService {
    constructor(
        private readonly http: HttpClient,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly dateService: DateService,
        private readonly productsService: ProductsService,
        private readonly translationService: TranslationService) { }


    getAccountMappings(serial: string): Observable<IAccountUnitMappingExtended[]> {
        return this.http.get<IAccountUnitMappingExtended[]>(`/api/foundation/units/${serial}/accountmappings`);
    }

    getConfigMappings(serial: string): Observable<IConfigMappings[]> {
        return this.http.get<IConfigMappings[]>(`/api/foundation/units/${serial}/configmappings`);
    }

    getFirmwareMappings(serial: string): Observable<IFirmwareMappings[]> {
        return this.http.get<IFirmwareMappings[]>(`/api/foundation/units/${serial}/firmwaremappings`);
    }

    getVehicleMappings(serial: string): Observable<IVehicleUnitMappingExtended[]> {
        return this.http.get<IVehicleUnitMappingExtended[]>(`/api/foundation/units/${serial}/vehiclemappings`);
    }

    applyVehicleMappingsToUnits(units: IUnit[], mappings: Map<string, IVehicleUnitMapping>): IUnit[] {
        return units.map(unit => ({
            ...unit,
            vehicleMapping: mappings.get(unit.Serial)
        }));
    }


    getProductsAndMap(peripherals: IUnit[]): Observable<IUnit[]> {
        return this.productsService.getProductUiViewModels().pipe(map(products => {
            this.mapProductsToUnits(peripherals, products);

            return peripherals;
        }))
    }

    mapProductsToUnits(units: IUnit[], products: IBasicProductModel[]): void {
        units.forEach(unit => {
            unit.Product = products.find(product => product.ProductId === unit.ProductId);

            this.setMetaDataOnUnit(unit);
        });
    }

    setMetaDataOnUnit(unit: IUnit, feature?: AccountFeatures): void {
        if (!unit) {
            console.error("No unit specified");
            return;
        }

        unit.primaryFeature = this.getSingleFeatureForUnit(unit);
        unit.productVendorString = this.productsService.getProductVendorString(unit.Product);


        unit.name = `${unit.productVendorString ? unit.productVendorString : ''} ${unit.Product?.Description}`;
        unit.icon = this.getIconForUnit(unit);
    }


    getSingleFeatureForUnit(unit: IUnit): AccountFeatures {

        if (!AccountFeatures[unit?.Product?.Features]) {
            return AccountFeatures.Default;
        }

        const features = this.accountFeatureService.findFeaturesFromFeaturesEnum(unit.Product.Features);

        if (features.indexOf(AccountFeatures.Camera) > -1) {
            return AccountFeatures.Camera;
        }

        return (features?.length) ? features[0] : null;
    }

    getIconForUnit(unit: IUnit): string {
        if (!unit) {
            return;
        }

        return this.accountFeatureService.getIconForFeature(unit.primaryFeature);
    }

    getDateDescription(date: string): string {
        switch (true) {
            case date && date === "0001-01-01T00:00:00Z":
                return this.translationService.t("FoundationHardware.Preexisting");
            case !date || date === "2200-01-01T00:00:00Z":
                return this.translationService.t("FoundationHardware.Current");
            default:
                return this.dateService.formatDate(date, {dateTime: true});
        }
    }
}
